import { Box, Stack, styled } from '@mui/material'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router'

import type { OwnershipDto } from '@hcr/api/consumer'
import { CardEmbedment, CardPrimary, CardVariant, COLORS, unit } from '@hcr/ui'

import { LINK_CONFIG } from '../../../../configs'
import { useUserDetails } from '../../../../contexts'
import { useLocale, useOwnersQuery } from '../../../../hooks'
import { createOwnersContent } from '../../../../utils'
import { Background, LayoutNavigation, LayoutNavigationError500, Metadata } from '../../../common'

import { Holdings, OwnerCard } from './common'
import { Benefits } from './common/benefits'
import { OwnerLoading } from './OwnerLoading'
import { OwnerUncontracted } from './OwnerUncontracted'

export const Owner: FC = () => {
  const { t } = useTranslation()
  const locale = useLocale()

  const userDetails = useUserDetails()
  const { is_villas, is_active_gold, is_b2b_gold, is_b2b_villas, is_b2b_basic } =
    (userDetails.data?.ownership as OwnershipDto) || {}

  const isVillasOwnerNoBenefits = Boolean(is_villas) && Boolean(!userDetails.data?.has_benefits)
  const isB2BOwner = is_b2b_basic || is_b2b_gold || is_b2b_villas

  const owners = useOwnersQuery(
    {
      hasBenefits: isVillasOwnerNoBenefits || Boolean(userDetails.data?.has_benefits),
      isContracted: true,
      isGoldOwner: !isVillasOwnerNoBenefits && (Boolean(is_active_gold) || Boolean(is_b2b_gold)),
      isVillasOwner: !isVillasOwnerNoBenefits && (Boolean(is_villas) || Boolean(is_b2b_villas)),
      isVillasOwnerNoBenefits,
    },
    { enabled: userDetails.isSuccess && userDetails.data?.has_active_contracts }
  )

  if (userDetails.isError || owners.isError) {
    return <LayoutNavigationError500 />
  }

  if (userDetails.isSuccess && !userDetails.data?.has_active_contracts) {
    return <OwnerUncontracted />
  }

  if (userDetails.isSuccess && owners.isSuccess) {
    const ownersContent = createOwnersContent({
      slidesMargin: unit(3),
      variant: CardVariant.Regular,
    })

    return (
      <LayoutNavigation>
        <Metadata title={t('page-title.owner')} />
        <Box paddingX={unit(3)} bgcolor={COLORS.white}>
          <OwnerCardBackground url={owners.data.imageUrl}>
            {userDetails.data?.has_benefits && <OwnerCard />}
          </OwnerCardBackground>
          <Stack gap={unit(3)} paddingY={unit(3)}>
            {isB2BOwner ? (
              <CardPrimary
                badge={null}
                description={t(
                  'owner.your-own-weeks-through-the-company-will-not-appear-on-your-own-holiday-page-if-you-want-to-rent-out-your-week-do-it-easily-using-the-form'
                )}
                embedment={CardEmbedment.Standalone}
                imageUrl={null}
                link={{
                  isExternal: true,
                  label: t('owner.for-rental'),
                  to: LINK_CONFIG.rentYourOwnHolidayWeek[locale.id],
                }}
                title={t('owner.your-holdings')}
                variant={CardVariant.Regular}
              />
            ) : (
              <Holdings />
            )}

            <Benefits />

            {owners.data.content.slice(1).map(ownersContent)}
          </Stack>
        </Box>

        <Outlet />
      </LayoutNavigation>
    )
  }

  return <OwnerLoading />
}

const OwnerCardBackground = styled(Background)`
  padding: ${unit(15.5)} ${unit(2.75)} ${unit(2.75)} ${unit(2.75)};
`
